import IMessage from "../interface/IMessage";
import debugLog from "../utils/debugLog";
import { reportAppError } from "../utils/reportAppError";

const FILE_ONLY_MESSAGE_PLACEHOLDER = " ";

interface IPendingUpload {
  id: number;
  text?: string;
  resolve: (result: boolean) => void;
}

let nextPendingUploadId = 0;
let fileUploadedSubscriptionRegistered = false;
const pendingUploads: IPendingUpload[] = [];

const getNormalizedText = (text?: string): string =>
  typeof text === "string" ? text.trim() : "";

const finalizePendingUpload = (pendingUpload: IPendingUpload): void => {
  const normalizedText = getNormalizedText(pendingUpload.text);
  const message = normalizedText || FILE_ONLY_MESSAGE_PLACEHOLDER;

  if (!normalizedText) {
    try {
      Genesys(
        "command",
        "MessagingService.sendMessage",
        { message },
        function () {
          return;
        },
        function () {
          return;
        },
      );
      pendingUpload.resolve(true);
    } catch (error) {
      reportAppError({
        scope: "uploadFile.finalizePendingUpload",
        error,
        metadata: { hasText: Boolean(normalizedText) },
      });
      pendingUpload.resolve(false);
    }

    return;
  }

  Genesys(
    "command",
    "MessagingService.sendMessage",
    { message },
    function () {
      pendingUpload.resolve(true);
    },
    function () {
      pendingUpload.resolve(false);
    },
  );
};

const ensureFileUploadedSubscription = (): void => {
  if (fileUploadedSubscriptionRegistered) return;

  Genesys("subscribe", "MessagingService.fileUploaded", () => {
    const pendingUpload = pendingUploads.shift();

    if (!pendingUpload) return;

    finalizePendingUpload(pendingUpload);
  });

  fileUploadedSubscriptionRegistered = true;
};

export const resetUploadFileState = (): void => {
  nextPendingUploadId = 0;
  fileUploadedSubscriptionRegistered = false;
  pendingUploads.length = 0;
};

export const __resetUploadFileStateForTests = resetUploadFileState;

const uploadFile = async (chatInput: IMessage): Promise<boolean> => {
  // Check if a file was provided.
  if (!chatInput.content) {
    debugLog("Skipping upload without file content.");
    return false;
  }

  try {
    // Use the provided file to create a DataTransfer object.
    const dt = new DataTransfer();
    dt.items.add(chatInput.content as File);
    const fileList = dt.files;

    ensureFileUploadedSubscription();

    return await new Promise((resolve) => {
      const pendingUploadId = nextPendingUploadId++;

      pendingUploads.push({
        id: pendingUploadId,
        text: chatInput.text,
        resolve,
      });

      // Use the Genesys API to upload the file.
      Genesys(
        "command",
        "MessagingService.requestUpload",
        {
          file: fileList,
        },
        function () {},
        function () {
          const pendingUploadIndex = pendingUploads.findIndex(
            (pendingUpload) => pendingUpload.id === pendingUploadId,
          );

          if (pendingUploadIndex !== -1) {
            pendingUploads.splice(pendingUploadIndex, 1);
          }

          resolve(false);
        },
      );
    });
  } catch (error) {
    reportAppError({ scope: "uploadFile", error });
    return false;
  }
};

export default uploadFile;
